import { ReactNode, useCallback, useState } from 'react';
import type { UseBoundStore, StoreApi } from 'zustand';
import { ComponentConfigParameterGroup } from 'common/stack/ComponentConfigDocuments';
import { ParameterGroupValues } from 'common/stack/ComponentWithValuesDocuments';
import { Accordion } from 'client/components/Accordion';
import { Section } from 'client/components/Section/Section';
import { Stack } from 'client/components/Stack';
import { ConfigurationItemInput } from './ConfigurationItemInput';

type State = { parameterGroups: ComponentConfigParameterGroup[]; values: ParameterGroupValues[] };

interface ConfigurationItemProps {
  store?: UseBoundStore<StoreApi<State>>;
  value?: ParameterGroupValues;
  parameterGroup: ComponentConfigParameterGroup;
  path?: string;
  children?: ReactNode;
  onChange: (groupLabel: string, parameterLabel: string, value?: string | number | boolean | null) => void;
  onToggle: (groupLabel: string, enabled: boolean) => void;
}

const ConfigurationItemContent = ({ store, value, parameterGroup, path, onChange, children }: Omit<ConfigurationItemProps, 'onToggle'>) => (
  <Stack>
    {parameterGroup.parameters.map((item, index) => (
      <ConfigurationItemInput
        key={item.identifier}
        store={store}
        value={value}
        path={path + '.parameters.' + index}
        readOnly={item.readOnly}
        parameterGroupIdentifier={parameterGroup.identifier}
        parameters={item}
        onChange={onChange}
      />
    ))}
    {children}
  </Stack>
);

const ConfigurationItemSection = ({ store, value, path, parameterGroup, onChange, children }: ConfigurationItemProps) => (
  <Section title={parameterGroup.name} subtitle={parameterGroup.description}>
    <ConfigurationItemContent {...{ store, value, path, parameterGroup, onChange }}>{children}</ConfigurationItemContent>
  </Section>
);

const ConfigurationItemAccordion = ({
  store,
  value,
  parameterGroup,
  onChange,
  onToggle,
  children,
  path,
  ...other
}: ConfigurationItemProps) => {
  let enabled = false;
  if (store) {
    enabled = store(
      useCallback(
        (state: State) => state.values.find((item) => item.identifier === parameterGroup.identifier)?.enabled || false,
        [parameterGroup.identifier]
      )
    );
  } else {
    enabled = value?.enabled || false;
  }

  // Only render content when accordion is opened for the first time
  const [isTouched, setIsTouched] = useState(enabled || false);

  const handleToggle = useCallback((enabled: boolean) => {
    onToggle(parameterGroup.identifier, enabled);
    setIsTouched(true);
  }, []);

  return (
    <Accordion title={parameterGroup.name} description={parameterGroup.description} open={enabled} onChange={handleToggle} {...other}>
      {isTouched && <ConfigurationItemContent {...{ store, parameterGroup, onChange }}>{children}</ConfigurationItemContent>}
    </Accordion>
  );
};

export const ConfigurationItem = (props: ConfigurationItemProps) => {
  const { parameterGroup } = props;
  if (!parameterGroup) return null;
  if (parameterGroup.toggleable) return <ConfigurationItemAccordion {...props} />;
  return <ConfigurationItemSection {...props} />;
};
